import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Params } from '@angular/router';
import { AssetDto, GalleryDto } from '@picflow/types';
import { defaultColorLabels } from 'src/app/configs/standard-presets';
import { filterAssets } from 'src/app/gallery-shared/lib/filter-assets';
import { parseAssetsFilters } from 'src/app/gallery-shared/lib/parse-assets-filter';
import { SortAssets } from 'src/app/gallery-shared/lib/sort-assets-sorting-and-grouping';
import { getPermissions } from 'src/app/lib/get-allowed-feedback-options';
import { AssetService } from 'src/app/services/asset.service';
import { SectionService } from 'src/app/services/section.service';
import { StateService } from 'src/app/services/state.service';
import { StorageService } from 'src/app/services/storage.service';

@Injectable({
  providedIn: 'root',
})
export class AssetPageDataService {
  constructor(
    private assetService: AssetService,
    private sectionService: SectionService,
    private storage: StorageService,
    private state: StateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  private async getAssets(galleryId: string): Promise<AssetDto[]> {
    if (isPlatformServer(this.platformId)) return [];
    const assets = await this.assetService.getAssetsForGallery(galleryId);
    return assets;
  }

  private async getSections(galleryId: string) {
    if (isPlatformServer(this.platformId)) return [];
    return this.sectionService.getSections(galleryId);
  }

  private async getAsset(assetId: string, galleryId: string) {
    try {
      return await this.assetService.getAsset(assetId, galleryId);
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
    }
  }

  public async get(gallery: GalleryDto, assetId: string, queryParams: Params) {
    let preferences = this.storage.get('advanced-mode-preferences');
    const sorting = preferences?.sortingAndGrouping || 'auto';
    const [assets, sections] = await Promise.all([this.getAssets(gallery.id), this.getSections(gallery.id)]);

    let colorLabels = gallery.color_labels;
    const permissions = getPermissions(gallery, this.state.plan);
    if (!permissions.asset.customLabels) colorLabels = defaultColorLabels;

    const filters = parseAssetsFilters(queryParams.filters) || [];

    const filteredAssets = SortAssets(
      filterAssets(assets, filters, queryParams.search),
      sorting,
      sections,
      colorLabels
    );

    const asset = await this.getAsset(assetId, gallery.id);
    if (isPlatformServer(this.platformId)) return { asset, assets: filteredAssets, sections };
    if (!asset) return;
    return { asset, assets: filteredAssets, sections };
  }
}
